import React, { useState } from "react";

import TailwickContactAvatar from "../ChatLayout/TailwickContactAvatar";
import ContactModal from "../ContactModal";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import { i18n } from "../../translate/i18n";
import ContactDrawerTags from "./ContactDrawerTags";
import ContactDrawerFunnel from "./ContactDrawerFunnel";
import ContactDrawerLanguage from "./ContactDrawerLanguage";
import ContactDrawerBookings from "./ContactDrawerBookings";
import TailwickCopyButton from "./TailwickCopyButton";
import ContactBlockButton from "./ContactBlockButton";

function IconPhone() {
	return (
		<svg className="size-4 shrink-0 text-default-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
			<rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
			<path d="M12 18h.01" />
		</svg>
	);
}

function IconMail() {
	return (
		<svg className="size-4 shrink-0 text-default-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
			<rect width="20" height="16" x="2" y="4" rx="2" />
			<path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
		</svg>
	);
}

function IconEdit() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
			<path d="M12 20h9" />
			<path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
		</svg>
	);
}

function InfoRow({ icon, children, copyContent }) {
	return (
		<div className="flex w-full max-w-full items-center justify-center gap-1.5 text-sm">
			{icon}
			<span className="min-w-0 truncate font-medium text-default-800">{children}</span>
			{copyContent ? <TailwickCopyButton content={copyContent} /> : null}
		</div>
	);
}

export default function ContactDrawerTailwick({
	open,
	handleDrawerClose,
	contact,
	loading,
	user,
	ticketId,
}) {
	const [modalOpen, setModalOpen] = useState(false);
	if (!open) return null;
	return (
		<aside
			className="tailwick-contact-drawer"
			aria-label={i18n.t("contactDrawer.header")}
		>
			<div className="tailwick-chat-header tailwick-contact-drawer__header">
				<div className="tailwick-chat-header-inner">
					<h6 className="card-title mb-0 min-w-0 flex-1 truncate text-default-800">
						{i18n.t("contactDrawer.header")}
					</h6>
					<button
						type="button"
						className="tailwick-contact-drawer__close btn btn-icon size-8 shrink-0 bg-default-150 text-default-600 hover:bg-default-200 hover:text-primary"
						onClick={handleDrawerClose}
						aria-label="Fechar"
					>
						<svg className="size-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
							<path d="M18 6 6 18" />
							<path d="m6 6 12 12" />
						</svg>
					</button>
				</div>
			</div>

			{loading ? (
				<ContactDrawerSkeleton tailwickLayout />
			) : (
				<div className="tailwick-contact-drawer__scroll">
					<div className="tailwick-contact-drawer__profile card-body">
						<TailwickContactAvatar
							size="md"
							className="tailwick-contact-avatar--drawer"
							contactId={contact.id}
							profilePicUrl={contact.profilePicUrl}
							isGroup={contact.isGroup}
						/>

						<div className="mt-4 flex w-full flex-col items-center gap-2 text-center">
							<InfoRow copyContent={contact.name}>{contact.name}</InfoRow>
							{(user.userSeeNumbers || user.profile === "admin") ? (
								<InfoRow icon={<IconPhone />} copyContent={contact.number}>
									{contact.number}
								</InfoRow>
							) : (
								<InfoRow icon={<IconPhone />}>
									Você não tem permissão <br/> para visualizar este número.
								</InfoRow>
							)}

							{contact.email && (
								<InfoRow icon={<IconMail />} copyContent={contact.email}>
									<a
										className="truncate text-default-800 hover:text-primary hover:underline"
										href={`mailto:${contact.email}`}
									>
										{contact.email}
									</a>
								</InfoRow>
							)}
						</div>

						<button
							type="button"
							className="btn btn-sm mt-4 border border-default-200 bg-transparent text-default-700 hover:border-primary/20 hover:bg-primary/10 hover:text-primary"
							onClick={() => setModalOpen(true)}
						>
							<IconEdit />
							{i18n.t("contactDrawer.buttons.edit")}
						</button>
						<ContactBlockButton contact={contact} ticketId={ticketId} />
					</div>

					<div className="tailwick-contact-drawer__sections card-body">
						<ContactDrawerTags contact={contact} />
						<ContactDrawerFunnel contact={contact} />
						<ContactDrawerLanguage contact={contact} />
						<ContactDrawerBookings contact={contact} ticketId={ticketId} />

						<div className="tailwick-contact-drawer__section tailwick-contact-drawer__section--extra">
							<ContactModal
								open={modalOpen}
								onClose={() => setModalOpen(false)}
								contactId={contact.id}
							/>
							<p className="tailwick-filter-panel__section-title">
								{i18n.t("contactDrawer.extraInfo")}
							</p>
							{contact?.extraInfo?.length > 0 ? (
								<div className="tailwick-contact-drawer__extra-list">
									{contact.extraInfo.map((info) => (
										<div key={info.id} className="tailwick-contact-drawer__extra-item card">
											<div className="flex items-start justify-between gap-2 px-3 py-2">
												<span className="text-xs font-semibold uppercase tracking-wide text-default-500">
													{info.name}
												</span>
												<TailwickCopyButton content={info.value} />
											</div>
											<div className="border-t border-default-200 px-3 py-2 text-sm text-default-700">
												<MarkdownWrapper>{info.value}</MarkdownWrapper>
											</div>
										</div>
									))}
								</div>
							) : (
								<p className="tailwick-filter-panel__empty">—</p>
							)}
						</div>
					</div>
				</div>
			)}
		</aside>
	);
}
